import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemText,
  Button,
  Stack
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import SEO from '../utils/SEO';

/**
 * Terms of Service Component
 * 
 * Comprehensive terms of service for TeachAssist platform.
 * Covers user responsibilities, service usage, and legal provisions.
 */
const TermsOfService: React.FC = () => {
  const navigate = useNavigate();

  const handleBackClick = () => {
    navigate(-1);
  };

  return (
    <>
      <SEO
        title="Terms of Service"
        description="Level Up Teaching terms of service - user responsibilities, service usage guidelines, and legal provisions for our educational platform."
        canonicalUrl="https://levelupteaching.ai/terms-of-service"
        keywords="terms of service, user agreement, legal terms, educational technology terms"
      />
      <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper elevation={2} sx={{ p: 4 }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h3" component="h1" gutterBottom color="primary">
            Terms of Service
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Last updated: {new Date().toLocaleDateString('en-US', { 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
            Effective Date: {new Date().toLocaleDateString('en-US', { 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}
          </Typography>
        </Box>

        <Divider sx={{ mb: 4 }} />

        {/* Introduction */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" component="h2" gutterBottom>
            1. Acceptance of Terms
          </Typography>
          <Typography variant="body1" paragraph>
            Welcome to TeachAssist ("we," "our," or "us"). These Terms of Service ("Terms") 
            govern your use of our educational platform service located at https://levelupteaching.ai 
            (the "Service") operated by Level Up Teaching, LLC.
          </Typography>
          <Typography variant="body1" paragraph>
            By accessing or using our Service, you agree to be bound by these Terms. If you 
            disagree with any part of these terms, then you may not access the Service.
          </Typography>
        </Box>

        {/* Description of Service */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" component="h2" gutterBottom>
            2. Description of Service
          </Typography>
          <Typography variant="body1" paragraph>
            TeachAssist is an AI-powered educational platform designed to help teachers and 
            educators create lesson plans, manage students, track progress, and deliver 
            personalized instruction. Our Service includes:
          </Typography>
          <List>
            <ListItem>
              <ListItemText primary="• AI-generated lesson plans and educational content" />
            </ListItem>
            <ListItem>
              <ListItemText primary="• Student management and grouping tools" />
            </ListItem>
            <ListItem>
              <ListItemText primary="• Assessment creation and grading capabilities" />
            </ListItem>
            <ListItem>
              <ListItemText primary="• Progress tracking and reporting features" />
            </ListItem>
            <ListItem>
              <ListItemText primary="• Integration with Google Classroom and other educational platforms" />
            </ListItem>
          </List>
        </Box>

        {/* User Accounts */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" component="h2" gutterBottom>
            3. User Accounts
          </Typography>
          <Typography variant="body1" paragraph>
            To use certain features of our Service, you must create an account. You agree to:
          </Typography>
          <List>
            <ListItem>
              <ListItemText primary="• Provide accurate, current, and complete information" />
            </ListItem>
            <ListItem>
              <ListItemText primary="• Maintain and update your account information" />
            </ListItem>
            <ListItem>
              <ListItemText primary="• Keep your account credentials secure and confidential" />
            </ListItem>
            <ListItem>
              <ListItemText primary="• Accept responsibility for all activities under your account" />
            </ListItem>
            <ListItem>
              <ListItemText primary="• Notify us immediately of any unauthorized account use" />
            </ListItem>
          </List>
        </Box>

        {/* Acceptable Use */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" component="h2" gutterBottom>
            4. Acceptable Use Policy
          </Typography>
          <Typography variant="body1" paragraph>
            You agree to use our Service only for lawful purposes and in accordance with these Terms. 
            You agree NOT to use the Service:
          </Typography>
          <List>
            <ListItem>
              <ListItemText primary="• In any way that violates applicable laws or regulations" />
            </ListItem>
            <ListItem>
              <ListItemText primary="• To transmit harmful, offensive, or inappropriate content" />
            </ListItem>
            <ListItem>
              <ListItemText primary="• To impersonate others or provide false information" />
            </ListItem>
            <ListItem>
              <ListItemText primary="• To interfere with or disrupt the Service or its servers" />
            </ListItem>
            <ListItem>
              <ListItemText primary="• To attempt unauthorized access to any part of the Service" />
            </ListItem>
            <ListItem>
              <ListItemText primary="• To use the Service for commercial purposes without permission" />
            </ListItem>
          </List>
        </Box>

        {/* Student Data and Privacy */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" component="h2" gutterBottom>
            5. Student Data and Educational Records
          </Typography>
          <Typography variant="body1" paragraph>
            We understand the sensitive nature of educational data. When using our Service:
          </Typography>
          <List>
            <ListItem>
              <ListItemText primary="• You remain the owner of all student data you input" />
            </ListItem>
            <ListItem>
              <ListItemText primary="• You must have proper authorization to input student information" />
            </ListItem>
            <ListItem>
              <ListItemText primary="• We comply with FERPA and other applicable education privacy laws" />
            </ListItem>
            <ListItem>
              <ListItemText primary="• Student data is used solely to provide educational services" />
            </ListItem>
            <ListItem>
              <ListItemText primary="• You can request deletion of student data at any time" />
            </ListItem>
          </List>
        </Box>

        {/* Intellectual Property */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" component="h2" gutterBottom>
            6. Intellectual Property Rights
          </Typography>
          <Typography variant="body1" paragraph>
            The Service and its original content, features, and functionality are owned by 
            Level Up Teaching, LLC and are protected by international copyright, trademark, 
            patent, trade secret, and other intellectual property laws.
          </Typography>
          <Typography variant="body1" paragraph>
            You retain ownership of any educational content you create using our Service. 
            However, you grant us a limited license to use this content solely to provide 
            and improve our Service.
          </Typography>
        </Box>

        {/* Service Availability */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" component="h2" gutterBottom>
            7. Service Availability
          </Typography>
          <Typography variant="body1" paragraph>
            We strive to provide reliable service but cannot guarantee:
          </Typography>
          <List>
            <ListItem>
              <ListItemText primary="• Uninterrupted or error-free operation" />
            </ListItem>
            <ListItem>
              <ListItemText primary="• Availability at all times" />
            </ListItem>
            <ListItem>
              <ListItemText primary="• Compatibility with all systems or devices" />
            </ListItem>
          </List>
          <Typography variant="body1" paragraph>
            We reserve the right to modify, suspend, or discontinue the Service with 
            reasonable notice when possible.
          </Typography>
        </Box>

        {/* Limitation of Liability */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" component="h2" gutterBottom>
            8. Limitation of Liability
          </Typography>
          <Typography variant="body1" paragraph>
            To the fullest extent permitted by law, Level Up Teaching, LLC shall not be 
            liable for any indirect, incidental, special, consequential, or punitive damages, 
            including but not limited to loss of profits, data, use, goodwill, or other 
            intangible losses resulting from your use of the Service.
          </Typography>
        </Box>

        {/* Termination */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" component="h2" gutterBottom>
            9. Termination
          </Typography>
          <Typography variant="body1" paragraph>
            We may terminate or suspend your account and access to the Service immediately, 
            without prior notice or liability, for any reason, including breach of these Terms.
          </Typography>
          <Typography variant="body1" paragraph>
            You may terminate your account at any time by contacting us or using the 
            account deletion feature in your settings.
          </Typography>
        </Box>

        {/* Changes to Terms */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" component="h2" gutterBottom>
            10. Changes to Terms
          </Typography>
          <Typography variant="body1" paragraph>
            We reserve the right to modify these Terms at any time. We will notify users 
            of any material changes by posting the new Terms on this page and updating 
            the "Last updated" date.
          </Typography>
        </Box>

        {/* Governing Law */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" component="h2" gutterBottom>
            11. Governing Law
          </Typography>
          <Typography variant="body1" paragraph>
            These Terms shall be governed by and construed in accordance with the laws 
            of the United States, without regard to its conflict of law provisions.
          </Typography>
        </Box>

        {/* Contact Information */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" component="h2" gutterBottom>
            12. Contact Information
          </Typography>
          <Typography variant="body1" paragraph>
            If you have any questions about these Terms of Service, please contact us:
          </Typography>
          <Box sx={{ pl: 2 }}>
            <Typography variant="body1">
              <strong>Email:</strong> legal@levelupteaching.ai
            </Typography>
            <Typography variant="body1">
              <strong>Website:</strong> https://levelupteaching.ai
            </Typography>
            <Typography variant="body1">
              <strong>Address:</strong> Level Up Teaching, LLC
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ my: 4 }} />

        {/* Footer Actions */}
        <Stack direction="row" spacing={2} justifyContent="center">
          <Button variant="outlined" onClick={handleBackClick}>
            Go Back
          </Button>
          <Button 
            variant="contained" 
            href="mailto:legal@levelupteaching.ai"
          >
            Contact Us
          </Button>
        </Stack>
      </Paper>
    </Container>
    </>
  );
};

export default TermsOfService;
